import CoreHttpHandler from '../../../http/services/CoreHttpHandler';

/**
 * Global "customer sidebar guards" slice.
 *
 * A small, reusable piece of global state that decides which conditional
 * links / dropdowns in the customer details sidebar (CustomerDetailSideBar)
 * should be visible. Each guard is just a named boolean flag, so gating a new
 * link later (on another API or another condition) needs no new plumbing:
 *
 *   1. dispatch `setSidebarGuard('<yourKey>', true/false)` (or write a thunk
 *      like `refreshCallGuard` that computes it from an API response), and
 *   2. read it in the sidebar with `selectSidebarGuard(state, '<yourKey>')`.
 *
 * Current guards:
 *   - 'call' : show the "Call" dropdown when at least one of the client's
 *              users (agents) has allow_calls === true.
 */

// ---- Action types ----
export const SET_SIDEBAR_GUARD = '[CUSTOMER SIDEBAR] SET GUARD';
export const SET_SIDEBAR_GUARDS = '[CUSTOMER SIDEBAR] SET GUARDS';
export const RESET_SIDEBAR_GUARDS = '[CUSTOMER SIDEBAR] RESET GUARDS';

// ---- Action creators ----
// Set a single guard flag. Coerced to a strict boolean.
export const setSidebarGuard = (key, value) => ({
	type: SET_SIDEBAR_GUARD,
	payload: { key, value: value === true }
});

// Set several guard flags at once, e.g. { call: true, foo: false }.
export const setSidebarGuards = guards => ({
	type: SET_SIDEBAR_GUARDS,
	payload: guards || {}
});

// Clear every guard (e.g. when leaving the customer details area).
export const resetSidebarGuards = () => ({
	type: RESET_SIDEBAR_GUARDS
});

/**
 * Thunk: recompute the "call" guard for a client from its user list.
 *
 * Fetches the client's users and turns the Call dropdown ON when ANY of them
 * has allow_calls === true, OFF otherwise. This is the single source of truth
 * for the flag, so it can be called from anywhere the answer might change:
 * the sidebar on mount, and the Users table right after a permission toggle.
 *
 * @param {string|number} clientId
 */
export const refreshCallGuard = clientId => dispatch => {
	if (!clientId) {
		dispatch(setSidebarGuard('call', false));
		return;
	}
	CoreHttpHandler.request(
		'CompanyAgent',
		'get_paginated',
		// High limit so the check covers all of the client's users, not just the
		// first page the Users table happens to render.
		{ client_id: clientId, limit: 1000, page: 0 },
		response => {
			const agentList = response?.data?.data?.agentList || [];
			const anyAllowsCalls = agentList.some(agent => agent?.allow_calls === true);
			dispatch(setSidebarGuard('call', anyAllowsCalls));
		},
		// On failure leave the current value untouched rather than guessing.
		() => {}
	);
};

// ---- Selectors ----
export const selectSidebarGuard = (state, key) => state?.customerSidebarGuards?.[key] === true;

// ---- Reducer ----
const initialState = {};

const customerSidebarGuards = (state = initialState, action) => {
	switch (action.type) {
		case SET_SIDEBAR_GUARD:
			return { ...state, [action.payload.key]: action.payload.value };
		case SET_SIDEBAR_GUARDS:
			return { ...state, ...action.payload };
		case RESET_SIDEBAR_GUARDS:
			return {};
		default:
			return state;
	}
};

export default customerSidebarGuards;
